import React, { useEffect, useState, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { auth, db, storage } from "/src/firebaseConfig.js";
import {
  getIdTokenResult,
  onAuthStateChanged,
  signOut,
  updateProfile,
  sendPasswordResetEmail,
  GoogleAuthProvider,
  OAuthProvider,
  linkWithPopup,
} from "firebase/auth";
import {
  collection,
  getDocs,
  query,
  where,
  limit,
  doc,
  getDoc,
  setDoc,
} from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import AppDrawer from "./ui/AppDrawer";
import { getAvatarUrl } from "../utils/avatar";
import ro from "/src/content/ro.json";
import { toDateNumber, toDisplayDate, toIsoDate } from "../utils/date";

const MyAccountProfileDrawer = ({ open, onClose }) => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [inscriere, setInscriere] = useState(null);
  const [abonament, setAbonament] = useState(null);
  const [loading, setLoading] = useState(true);
  const [progressPickerOpen, setProgressPickerOpen] = useState(false);
  const [attendanceOpen, setAttendanceOpen] = useState(false);
  const [presenceDates, setPresenceDates] = useState([]);
  const [authMessage, setAuthMessage] = useState("");
  const [sendingReset, setSendingReset] = useState(false);
  const [linkingGoogle, setLinkingGoogle] = useState(false);
  const [linkingApple, setLinkingApple] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoMessage, setPhotoMessage] = useState("");
  const [photoModalOpen, setPhotoModalOpen] = useState(false);
  const fileInputRef = useRef(null);
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  });
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        await currentUser.reload();
        const provider = currentUser.providerData?.[0] || {};
        const providerPhoto = provider.photoURL || currentUser.photoURL || null;
        const providerName =
          provider.displayName || currentUser.displayName || null;

        if (
          (providerPhoto && currentUser.photoURL !== providerPhoto) ||
          (providerName && currentUser.displayName !== providerName)
        ) {
          try {
            await updateProfile(currentUser, {
              photoURL: providerPhoto || undefined,
              displayName: providerName || undefined,
            });
            await currentUser.reload();
          } catch (err) {
            console.error("Eroare update profil:", err);
          }
        }

        if (currentUser.uid) {
          try {
            await setDoc(
              doc(db, "users", currentUser.uid),
              {
                uid: currentUser.uid,
                email: currentUser.email || null,
                name: providerName || currentUser.displayName || null,
                photoURL: providerPhoto || currentUser.photoURL || null,
                provider: provider.providerId || currentUser.providerId || null,
              },
              { merge: true }
            );
          } catch (err) {
            console.error("Eroare sync profil in users:", err);
          }
        }

        setUser({
          ...currentUser,
          photoURL: getAvatarUrl({
            photoURL: currentUser.photoURL || providerPhoto,
            email: currentUser.email,
            name: currentUser.displayName,
          }),
        });

        try {
          const tokenResult = await getIdTokenResult(currentUser, true);
          const isClaimAdmin = Boolean(tokenResult?.claims?.admin);
          const email = (currentUser.email || "").toLowerCase();
          const isFallbackAdmin =
            email === "razvanduta21@gmail.com" ||
            email === "stefanalexandru@traseuaplicativ.com" ||
            email === "alexandrustefan.97@yahoo.com";
          setIsAdmin(isClaimAdmin || isFallbackAdmin);
        } catch (err) {
          console.error("Eroare la verificarea rolului admin:", err);
          const email = (currentUser.email || "").toLowerCase();
          const isFallbackAdmin =
            email === "razvanduta21@gmail.com" ||
            email === "stefanalexandru@traseuaplicativ.com" ||
            email === "alexandrustefan.97@yahoo.com";
          setIsAdmin(isFallbackAdmin);
        }

        await loadUserData(currentUser);
      } else {
        setUser(null);
        setIsAdmin(false);
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  const loadUserData = async (currentUser) => {
    try {
      setLoading(true);

      const email = currentUser?.email || "";
      const uid = currentUser?.uid || "";
      let profileDoc = null;
      if (uid) {
        const profileSnap = await getDoc(doc(db, "users", uid));
        profileDoc = profileSnap.exists() ? profileSnap.data() : null;
      }

      const inscrieriRef = collection(db, "inscrieri_traseu");
      const inscrieriQ = query(inscrieriRef, where("email", "==", email));
      const inscrieriSnap = await getDocs(inscrieriQ);
      const inscriereData = inscrieriSnap.docs[0]?.data() || null;

      const abonRef = collection(db, "abonamente");
      let abonSnap = null;
      if (uid) {
        abonSnap = await getDocs(
          query(abonRef, where("userId", "==", uid), limit(1))
        );
      }
      if (!abonSnap || abonSnap.empty) {
        const emailKey = (email || "").toLowerCase();
        abonSnap = await getDocs(
          query(abonRef, where("email", "==", emailKey), limit(1))
        );
        if (abonSnap.empty && emailKey && emailKey !== email) {
          abonSnap = await getDocs(
            query(abonRef, where("email", "==", email), limit(1))
          );
        }
      }
      setAbonament(abonSnap.docs[0]?.data() || null);

      const resolvedPhoto = getAvatarUrl({
        photoURL:
          currentUser?.photoURL ||
          currentUser?.providerData?.[0]?.photoURL ||
          profileDoc?.photoURL,
        email: currentUser?.email || profileDoc?.email,
        name:
          currentUser?.displayName ||
          profileDoc?.displayName ||
          profileDoc?.name,
      });
      setUser((prev) =>
        prev
          ? {
              ...prev,
              photoURL: resolvedPhoto,
              displayName:
                prev.displayName ||
                profileDoc?.displayName ||
                profileDoc?.name,
            }
          : prev
      );

      if (inscriereData?.tipTraseu && inscriereData?.dataTraseu) {
        const progrRef = collection(db, "stabilire_program");
        const tip = inscriereData.tipTraseu;
        const targetDate = inscriereData.dataTraseu;
        let prog = null;

        const directSnap = await getDocs(
          query(
            progrRef,
            where("tipProgram", "==", tip),
            where("data", "==", targetDate),
            limit(1)
          )
        );
        if (!directSnap.empty) {
          prog = directSnap.docs[0]?.data();
        } else {
          const altDate = toIsoDate(targetDate);
          if (altDate && altDate !== targetDate) {
            const altSnap = await getDocs(
              query(
                progrRef,
                where("tipProgram", "==", tip),
                where("data", "==", altDate),
                limit(1)
              )
            );
            if (!altSnap.empty) {
              prog = altSnap.docs[0]?.data();
            }
          }
        }

        if (!prog) {
          const tipSnap = await getDocs(
            query(progrRef, where("tipProgram", "==", tip))
          );
          const targetNum = toDateNumber(targetDate);
          const match = tipSnap.docs.find(
            (docSnap) => toDateNumber(docSnap.data()?.data) === targetNum
          );
          prog = match?.data() || null;
        }

        setInscriere({
          ...inscriereData,
          locatie: prog?.locatie || inscriereData.locatie || "Nespecificat",
          ora: prog?.ora || inscriereData.ora || "N/A",
        });
      } else {
        setInscriere(inscriereData);
      }
    } catch (err) {
      console.error("Eroare Firestore:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) {
      setProgressPickerOpen(false);
      setAttendanceOpen(false);
      setPhotoModalOpen(false);
    }
  }, [open]);

  useEffect(() => {
    if (!attendanceOpen || !user?.email) return;
    const loadPresence = async () => {
      try {
        const results = [];
        const emailQuery = query(
          collection(db, "prezente"),
          where("userEmail", "==", user.email)
        );
        const emailSnap = await getDocs(emailQuery);
        emailSnap.forEach((d) => results.push(d.data()));

        if (user.uid) {
          const idSnap = await getDocs(
            query(collection(db, "prezente"), where("userId", "==", user.uid))
          );
          idSnap.forEach((d) => results.push(d.data()));
        }

        setPresenceDates(results);
      } catch (err) {
        console.error("Eroare prezente:", err);
        setPresenceDates([]);
      }
    };
    loadPresence();
  }, [attendanceOpen, user]);

  const handleLogout = async () => {
    await signOut(auth);
    if (onClose) onClose();
  };

  const handlePhotoUpload = async (event) => {
    const current = auth.currentUser;
    if (!current) return;
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setPhotoMessage(ro.profile.photo.invalidType);
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setPhotoMessage(ro.profile.photo.tooLarge);
      return;
    }

    setPhotoMessage("");
    setUploadingPhoto(true);
    try {
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
      const filePath = `profile_images/${current.uid}/${Date.now()}_${safeName}`;
      const storageRef = ref(storage, filePath);
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);

      await updateProfile(current, { photoURL: downloadURL });
      await current.reload();

      await setDoc(
        doc(db, "users", current.uid),
        { photoURL: downloadURL },
        { merge: true }
      );

      setUser((prev) =>
        prev
          ? {
              ...prev,
              photoURL: downloadURL,
            }
          : prev
      );
      setPhotoMessage(ro.profile.photo.uploadSuccess);
      setPhotoModalOpen(false);
    } catch (err) {
      console.error("Eroare upload poza:", err);
      setPhotoMessage(ro.profile.photo.uploadError);
    } finally {
      setUploadingPhoto(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const providerIds =
    auth.currentUser?.providerData?.map((p) => p.providerId) ||
    user?.providerData?.map((p) => p.providerId) ||
    [];
  const hasPasswordProvider = providerIds.includes("password");
  const hasGoogleProvider = providerIds.includes("google.com");
  const hasAppleProvider = providerIds.includes("apple.com");
  const showSetPassword =
    !hasPasswordProvider && (hasGoogleProvider || hasAppleProvider);
  const showLinkGoogle = hasPasswordProvider && !hasGoogleProvider;
  const showLinkApple = false;
  const showSecurityCard =
    showSetPassword || showLinkGoogle || showLinkApple || Boolean(authMessage);

  const handleSetPassword = async () => {
    const email = auth.currentUser?.email || user?.email;
    if (!email) return;
    setAuthMessage("");
    setSendingReset(true);
    try {
      await sendPasswordResetEmail(auth, email);
      setAuthMessage(ro.profile.authMessages.setPassword);
    } catch (err) {
      console.error("Eroare resetare parola:", err);
      setAuthMessage(ro.profile.authMessages.setPasswordError);
    } finally {
      setSendingReset(false);
    }
  };

  const handleLinkGoogle = async () => {
    const current = auth.currentUser;
    if (!current) return;
    setAuthMessage("");
    setLinkingGoogle(true);
    try {
      const provider = new GoogleAuthProvider();
      provider.addScope("profile");
      provider.addScope("email");
      const result = await linkWithPopup(current, provider);
      const linkedUser = result.user;
      await linkedUser.reload();

      const providerData =
        linkedUser.providerData?.find((p) => p.providerId === "google.com") ||
        {};
      const tokenResponse = result?._tokenResponse || {};
      const providerPhoto =
        linkedUser.photoURL ||
        providerData.photoURL ||
        tokenResponse.photoUrl ||
        tokenResponse.photoURL ||
        null;
      const providerName =
        linkedUser.displayName ||
        providerData.displayName ||
        tokenResponse.displayName ||
        null;

      if (providerPhoto || providerName) {
        await updateProfile(linkedUser, {
          photoURL: providerPhoto || undefined,
          displayName: providerName || undefined,
        });
        await linkedUser.reload();
      }

      if (linkedUser.uid) {
        await setDoc(
          doc(db, "users", linkedUser.uid),
          {
            uid: linkedUser.uid,
            email: linkedUser.email || null,
            name: providerName || linkedUser.displayName || null,
            photoURL: providerPhoto || linkedUser.photoURL || null,
            provider: "google.com",
          },
          { merge: true }
        );
      }

      setUser({
        ...linkedUser,
        photoURL: getAvatarUrl({
          photoURL: linkedUser.photoURL || providerPhoto,
          email: linkedUser.email,
          name: linkedUser.displayName,
        }),
      });
      setAuthMessage(ro.profile.authMessages.linkGoogleSuccess);
    } catch (err) {
      console.error("Eroare link Google:", err);
      const message =
        err?.code === "auth/credential-already-in-use"
          ? ro.profile.authMessages.linkGoogleInUse
          : ro.profile.authMessages.linkGoogleError;
      setAuthMessage(message);
    } finally {
      setLinkingGoogle(false);
    }
  };

  const handleLinkApple = async () => {
    const current = auth.currentUser;
    if (!current) return;
    setAuthMessage("");
    setLinkingApple(true);
    try {
      const provider = new OAuthProvider("apple.com");
      provider.addScope("email");
      provider.addScope("name");
      const result = await linkWithPopup(current, provider);
      const linkedUser = result.user;
      await linkedUser.reload();

      const providerData =
        linkedUser.providerData?.find((p) => p.providerId === "apple.com") ||
        {};
      const tokenResponse = result?._tokenResponse || {};
      const providerPhoto =
        linkedUser.photoURL ||
        providerData.photoURL ||
        tokenResponse.photoUrl ||
        tokenResponse.photoURL ||
        null;
      const providerName =
        linkedUser.displayName ||
        providerData.displayName ||
        tokenResponse.displayName ||
        null;

      if (providerPhoto || providerName) {
        await updateProfile(linkedUser, {
          photoURL: providerPhoto || undefined,
          displayName: providerName || undefined,
        });
        await linkedUser.reload();
      }

      if (linkedUser.uid) {
        await setDoc(
          doc(db, "users", linkedUser.uid),
          {
            uid: linkedUser.uid,
            email: linkedUser.email || null,
            name: providerName || linkedUser.displayName || null,
            photoURL: providerPhoto || linkedUser.photoURL || null,
            provider: "apple.com",
          },
          { merge: true }
        );
      }

      setUser({
        ...linkedUser,
        photoURL: getAvatarUrl({
          photoURL: linkedUser.photoURL || providerPhoto,
          email: linkedUser.email,
          name: linkedUser.displayName,
        }),
      });
      setAuthMessage(ro.profile.authMessages.linkAppleSuccess);
    } catch (err) {
      console.error("Eroare link Apple:", err);
      const message =
        err?.code === "auth/credential-already-in-use"
          ? ro.profile.authMessages.linkAppleInUse
          : ro.profile.authMessages.linkAppleError;
      setAuthMessage(message);
    } finally {
      setLinkingApple(false);
    }
  };

  const progressCategories = [
    { key: "mai", label: "MAI" },
    { key: "mapn", label: "MApN" },
    { key: "anp", label: "ANP" },
    { key: "isu", label: "ISU" },
  ];

  const handleProgressNavigation = (key) => {
    setProgressPickerOpen(false);
    if (onClose) onClose();
    navigate(`/progres/${key}`);
  };

  const presenceStatus = useMemo(() => {
    const map = {};
    (presenceDates || []).forEach((item) => {
      const iso = toIsoDate(item.data || item.date || item);
      if (!iso) return;
      let status = (item.status || "").toLowerCase();
      if (!status) status = "prezent";
      if (status !== "prezent" && status !== "absent") return;
      if (map[iso] === "prezent") return;
      map[iso] = status;
    });
    return map;
  }, [presenceDates]);

  const changeMonth = (delta) => {
    const next = new Date(calendarMonth.year, calendarMonth.month + delta, 1);
    setCalendarMonth({ year: next.getFullYear(), month: next.getMonth() });
  };

  const monthNames = ro.profile.attendance.months;

  const calendarDays = (() => {
    const { year, month } = calendarMonth;
    const firstDay = new Date(year, month, 1);
    const startOffset = (firstDay.getDay() + 6) % 7;
    const totalDays = new Date(year, month + 1, 0).getDate();
    const startNum = toDateNumber(abonament?.dataInceput || "");
    const endNum = toDateNumber(abonament?.dataFinal || abonament?.dataExpirare || "");
    const arr = [];
    for (let i = 0; i < startOffset; i++) arr.push(null);
    for (let d = 1; d <= totalDays; d++) {
      const iso = `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
      const dateNum = new Date(year, month, d).getTime();
      const inRange = startNum && endNum ? dateNum >= startNum && dateNum <= endNum : false;
      const status = presenceStatus[iso];
      arr.push({ day: d, iso, inRange, status });
    }
    return arr;
  })();

  const footer = user ? (
    <div className="drawer-actions">
      <button
        onClick={() => setProgressPickerOpen(true)}
        className="drawer-cta primary"
      >
        {ro.profile.actions.progress}
      </button>
      <button
        onClick={() => setAttendanceOpen(true)}
        className="drawer-cta secondary"
      >
        {ro.profile.actions.attendance}
      </button>
      <button onClick={handleLogout} className="drawer-link danger">
        {ro.profile.actions.logout}
      </button>
    </div>
  ) : null;

  const subscriptionEndNum = toDateNumber(
    abonament?.dataFinal || abonament?.dataExpirare || ""
  );
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const daysLeft =
    subscriptionEndNum != null
      ? Math.ceil((subscriptionEndNum - today.getTime()) / 86400000)
      : null;
  const isExpiring = daysLeft != null && daysLeft <= 3 && daysLeft >= 0;

  return (
    <>
      <AppDrawer
        open={open}
        onClose={onClose}
        title={ro.profile.title}
        allowBackdropClose={!progressPickerOpen && !attendanceOpen}
        className="profile-drawer"
        footer={footer}
      >
        {loading ? (
          <p className="drawer-muted">{ro.profile.loading}</p>
        ) : user ? (
          <>
            <div className="drawer-card drawer-profile">
              <button
                type="button"
                className="drawer-avatar drawer-avatar-sm"
                onClick={() => {
                  setPhotoMessage("");
                  setPhotoModalOpen(true);
                }}
                aria-label={ro.profile.addPhotoAria}
                style={{ padding: 0, background: "none" }}
              >
                <img
                  src={user.photoURL}
                  alt={ro.profile.profileAlt}
                  className="w-full h-full object-cover"
                  onError={(e) => (e.target.src = "/images/imgaine1.svg")}
                />
              </button>
              <div>
                <p className="drawer-name text-base">{user.displayName}</p>
                <p className="drawer-sub text-xs">{user.email}</p>
              </div>
            </div>

            {isAdmin && (
              <button
                className="drawer-cta primary"
                onClick={() => {
                  if (onClose) onClose();
                  navigate("/adminpage");
                }}
              >
                {ro.profile.adminPanel}
              </button>
            )}

            {showSecurityCard && (
              <div className="drawer-card">
                <p className="drawer-card-title">{ro.profile.securityTitle}</p>
                <div className="flex flex-col gap-2">
                  {showSetPassword && (
                    <button
                      className="drawer-cta secondary"
                      onClick={handleSetPassword}
                      disabled={sendingReset}
                    >
                      {sendingReset
                        ? ro.profile.sending
                        : ro.profile.setPassword}
                    </button>
                  )}
                  {showLinkGoogle && (
                    <button
                      className="drawer-cta primary"
                      onClick={handleLinkGoogle}
                      disabled={linkingGoogle}
                    >
                      {linkingGoogle
                        ? ro.profile.linking
                        : ro.profile.linkGoogle}
                    </button>
                  )}
                  {showLinkApple && (
                    <button
                      className="drawer-cta primary"
                      onClick={handleLinkApple}
                      disabled={linkingApple}
                    >
                      {linkingApple
                        ? ro.profile.linking
                        : ro.profile.linkApple}
                    </button>
                  )}
                  {authMessage && (
                    <p className="drawer-sub text-sm">{authMessage}</p>
                  )}
                </div>
              </div>
            )}

            <div className="drawer-card">
              <p className="drawer-card-title">{ro.profile.generalTitle}</p>
              {inscriere ? (
                <div className="drawer-grid">
                  <div className="drawer-list-item">
                    <span className="drawer-label">{ro.profile.labels.varsta}</span>
                    <span className="drawer-value">
                      {inscriere.varsta ? `${inscriere.varsta} ani` : "-"}
                    </span>
                  </div>
                  <div className="drawer-list-item">
                    <span className="drawer-label">{ro.profile.labels.greutate}</span>
                    <span className="drawer-value">
                      {inscriere.greutate ? `${inscriere.greutate} kg` : "-"}
                    </span>
                  </div>
                  <div className="drawer-list-item">
                    <span className="drawer-label">{ro.profile.labels.inaltime}</span>
                    <span className="drawer-value">
                      {inscriere.inaltime ? `${inscriere.inaltime} cm` : "-"}
                    </span>
                  </div>
                </div>
              ) : (
                <p className="drawer-sub">
                  {ro.profile.generalMissing}{" "}
                  <span style={{ color: "var(--color-accent)", fontWeight: 600 }}>
                    {ro.profile.generalCta}
                  </span>
                </p>
              )}
            </div>

            <div className="drawer-card">
              <p className="drawer-card-title">{ro.profile.subscriptionTitle}</p>
              {abonament ? (
                <p className="text-sm">
                  <span
                    className={`subscription-status ${
                      isExpiring ? "is-expiring" : "is-active"
                    }`}
                  >
                    {ro.profile.subscriptionActive}
                  </span>{" "}
                  {ro.profile.subscriptionUntil}{" "}
                  <span className="font-semibold">
                    {toDisplayDate(
                      abonament.dataFinal || abonament.dataExpirare || "N/A"
                    )}
                  </span>
                </p>
              ) : (
                <p className="drawer-sub">{ro.profile.subscriptionNone}</p>
              )}
            </div>

            <div className="drawer-card">
              <p className="drawer-card-title">{ro.profile.lastSignupTitle}</p>
              {inscriere ? (
                <div className="drawer-list">
                  <div className="drawer-list-item">
                    {ro.profile.labels.tipTraseu} {inscriere.tipTraseu}
                  </div>
                  <div className="drawer-list-item">
                    {ro.profile.labels.data} {toDisplayDate(inscriere.dataTraseu)}
                  </div>
                  <div className="drawer-list-item">
                    {ro.profile.labels.locatie} {inscriere.locatie || ro.profile.locationFallback}
                  </div>
                  <div className="drawer-list-item">
                    {ro.profile.labels.ora} {inscriere.ora || ro.profile.hourFallback}
                  </div>
                </div>
              ) : (
                <p className="drawer-sub">
                  {ro.profile.lastSignupNone}
                </p>
              )}
            </div>
          </>
        ) : (
          <p className="drawer-muted">{ro.profile.notConnected}</p>
        )}

        {photoModalOpen && (
          <div
            className="attendance-calendar-overlay"
            style={{ backgroundColor: "var(--drawer-overlay)" }}
          >
            <div className="drawer-card drawer-modal attendance-calendar-modal w-full max-w-md p-6 space-y-4 text-center">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1">
                  <p className="drawer-kicker">{ro.profile.photo.title}</p>
                  <h3 className="text-2xl font-bold">{ro.profile.photo.headline}</h3>
                  <p className="drawer-muted mt-1">{ro.profile.photo.hint}</p>
                </div>
                <button
                  className="drawer-close"
                  onClick={() => setPhotoModalOpen(false)}
                  aria-label={ro.profile.photo.closeAria}
                >
                  X
                </button>
              </div>

              <div className="flex flex-col gap-2">
                <button
                  type="button"
                  className="drawer-cta primary"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingPhoto}
                >
                  {uploadingPhoto ? ro.profile.photo.uploading : ro.profile.photo.choose}
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handlePhotoUpload}
                />
                {photoMessage && (
                  <p className="drawer-sub text-sm">{photoMessage}</p>
                )}
              </div>
            </div>
          </div>
        )}

        {attendanceOpen && (
          <div
            className="attendance-calendar-overlay"
            style={{ backgroundColor: "var(--drawer-overlay)" }}
          >
            <div className="drawer-card drawer-modal attendance-calendar-modal w-full max-w-2xl p-6 space-y-5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="drawer-kicker" style={{ color: "var(--color-success)" }}>
                    {ro.profile.attendance.title}
                  </p>
                  <h3 className="text-2xl font-bold attendance-calendar-title">
                    {ro.profile.attendance.headline}
                  </h3>
                  <p className="drawer-muted mt-1">{ro.profile.attendance.desc}</p>
                </div>
                <button
                  className="drawer-close"
                  onClick={() => setAttendanceOpen(false)}
                  aria-label={ro.profile.attendance.close}
                >
                  X
                </button>
              </div>

              <div className="flex items-center justify-between gap-3 flex-wrap">
                <button
                  onClick={() => changeMonth(-1)}
                  className="drawer-close attendance-calendar-arrow"
                  aria-label={ro.profile.attendance.prevMonthAria}
                >
                  {'<'}
                </button>
                <p className="text-lg font-semibold">
                  {monthNames[calendarMonth.month]} {calendarMonth.year}
                </p>
                <button
                  onClick={() => changeMonth(1)}
                  className="drawer-close attendance-calendar-arrow"
                  aria-label={ro.profile.attendance.nextMonthAria}
                >
                  {'>'}
                </button>
              </div>

              <div className="grid grid-cols-7 gap-2 text-center text-sm">
                {ro.profile.attendance.weekdays.map((wd) => (
                  <div key={wd} className="font-semibold" style={{ color: "var(--color-secondary-text)" }}>
                    {wd}
                  </div>
                ))}
                {calendarDays.map((cell, idx) => {
                  if (!cell) return <div key={`empty-${idx}`} />;
                  let background = "var(--drawer-card)";
                  let color = "var(--color-text)";
                  if (cell.status === "prezent") {
                    background = "var(--color-success-soft)";
                    color = "var(--color-success)";
                  } else if (cell.status === "absent") {
                    background = "var(--color-danger-soft)";
                    color = "var(--color-danger)";
                  } else if (cell.inRange) {
                    background = "rgba(249,115,22,0.15)";
                    color = "#f97316";
                  }
                  return (
                    <div
                      key={cell.iso}
                      className="rounded-lg font-semibold"
                      style={{
                        padding: "12px 0",
                        border: "1px solid var(--drawer-border)",
                        backgroundColor: background,
                        color,
                      }}
                    >
                      {cell.day}
                    </div>
                  );
                })}
              </div>

              <button
                onClick={() => setAttendanceOpen(false)}
                className="drawer-cta secondary"
              >
                {ro.profile.attendance.close}
              </button>
            </div>
          </div>
        )}

        {progressPickerOpen && (
          <div
            className="attendance-calendar-overlay"
            style={{ backgroundColor: "var(--drawer-overlay)" }}
          >
            <div className="drawer-card drawer-modal attendance-calendar-modal progress-picker-modal w-full max-w-md p-6 space-y-5 text-center">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1">
                  <p className="drawer-kicker">{ro.profile.progress.title}</p>
                  <h3 className="text-2xl font-bold progress-picker-title">
                    {ro.profile.progress.headline}
                  </h3>
                  <p className="drawer-muted mt-1">{ro.profile.progress.desc}</p>
                </div>
                <button
                  className="drawer-close"
                  onClick={() => setProgressPickerOpen(false)}
                  aria-label={ro.profile.progress.cancel}
                >
                  X
                </button>
              </div>

              <div className="grid grid-cols-2 gap-3 justify-center">
                {progressCategories.map((cat) => (
                  <button
                    key={cat.key}
                    onClick={() => handleProgressNavigation(cat.key)}
                    className="drawer-cta primary"
                  >
                    {cat.label}
                  </button>
                ))}
              </div>

              <button
                onClick={() => setProgressPickerOpen(false)}
                className="drawer-cta secondary"
              >
                {ro.profile.progress.cancel}
              </button>
            </div>
          </div>
        )}
      </AppDrawer>
    </>
  );
};

export default MyAccountProfileDrawer;



